import { useEffect, useState } from 'react';

/**
 * Channel/influencer avatar thumbnail with a consistent image-load fallback.
 *
 * Every channel thumbnail across the admin shares this fallback logic: the image
 * renders immediately and swaps to a user glyph only when it fails to load. It is
 * state-based (not imperative DOM toggling), so the same instance re-rendered with
 * a different but valid `src` — a panel channel switch, a sibling switch, a
 * re-sorted or virtualized row — correctly re-shows the image instead of sticking
 * on the fallback glyph forever (the latent bug in the old `onError` DOM-toggle).
 *
 * The skin stays per-site via `className` / `fallbackClassName`, which default to
 * the shared `.influencer-thumb` / `.influencer-thumb-fallback` 24px round
 * treatment. Pass `size` for the few callers that size via inline style instead.
 */
export function ChannelThumbnail({
  src,
  className = 'influencer-thumb',
  fallbackClassName = 'influencer-thumb-fallback',
  onClick,
  referrerPolicy,
  size,
}: {
  src?: string | null;
  className?: string;
  fallbackClassName?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  referrerPolicy?: React.HTMLAttributeReferrerPolicy;
  size?: number;
}) {
  const [broken, setBroken] = useState(false);
  // Reset on src change so a new valid URL isn't masked by a prior load error
  // on the same component instance.
  useEffect(() => {
    setBroken(false);
  }, [src]);

  const showImg = !!src && !broken;
  const sizeStyle = size != null ? { width: size, height: size } : undefined;

  return showImg ? (
    <img
      src={src ?? undefined}
      alt=""
      className={className}
      style={sizeStyle}
      onClick={onClick}
      referrerPolicy={referrerPolicy}
      onError={() => setBroken(true)}
    />
  ) : (
    <span className={fallbackClassName} style={sizeStyle} onClick={onClick}>
      <i className="fa fa-user" />
    </span>
  );
}
